import React from 'react';
import { useFormState } from 'react-final-form';
import {
  GOVERNMENT_INSURANCES,
  IN_NETWORK_INSURANCES,
  RELATIONSHIP_OPTIONS,
  OTHER_OPTION,
} from '@tempus/patient-forms-service-shared';
import { InsuranceType } from 'requisition-service-shared';
import { Typography } from '@mui/material';
import { required } from '../../../shared/form/validators';
import FormSingleSelectDropdown from '../../../shared/form/FormSingleSelectDropdown';
import FormTextInput from '../../../shared/form/FormTextInput';
import { createUseStyles } from 'react-jss';
import { GrayscalePalette } from '@tcl-boron-colors/colors';

interface InsuranceInformationSubformProps {
  insuranceType: InsuranceType;
  onCompanyNameChange: (value?: string) => void;
}

const useStyles = createUseStyles({
  container: {
    padding: '16px',
    border: `1px solid ${GrayscalePalette[400]}`,
    gap: '16px',
    display: 'flex',
    flexDirection: 'column',
  },
});

export const InsuranceInformationSubform: React.FC<InsuranceInformationSubformProps> = ({
  insuranceType,
  onCompanyNameChange,
}) => {
  const { values } = useFormState();
  const { insuranceCompanyName } = values;
  const classes = useStyles();

  const shouldShowInsuranceFields = insuranceCompanyName && insuranceCompanyName !== OTHER_OPTION.value;
  const insuranceOptions = (insuranceType === InsuranceType.Private ? IN_NETWORK_INSURANCES : GOVERNMENT_INSURANCES)
    .map((insurance) => ({
      label: insurance,
      value: insurance,
    }))
    .concat(insuranceType === InsuranceType.Private ? [OTHER_OPTION] : []);
  const isGroupNumberRequired = insuranceType !== InsuranceType.Public;

  return (
    <div className={classes.container}>
      <Typography variant="h6" component="h3">
        Insurance Information
      </Typography>

      <FormSingleSelectDropdown
        name="insuranceCompanyName"
        label="Insurance Plan Type*"
        validate={required}
        placeholder="Select Insurance Plan Type"
        options={insuranceOptions}
        data-testid="insurance-company-dropdown"
        onChangeSideEffects={onCompanyNameChange}
      />

      {shouldShowInsuranceFields && (
        <>
          {insuranceType === InsuranceType.Public && (
            <FormTextInput
              label="Insurance Provider Name*"
              placeholder="Enter insurance provider name"
              name="insuranceProviderName"
              validate={(value: string | null) => {
                if (!value) return required(value);
                const CHARACTER_LIMIT = 40;
                if (value.length > CHARACTER_LIMIT) {
                  return `Insurance Provider Name must be less than ${CHARACTER_LIMIT} characters`;
                }
              }}
              data-testid="insurance-provider-name"
            />
          )}
          <FormTextInput
            label="Policy Number*"
            placeholder="Enter policy number"
            name="policyNumber"
            validate={required}
            data-testid="policy-number"
          />

          <FormTextInput
            label={`Group Number${isGroupNumberRequired ? '*' : ''}`}
            placeholder="Enter group number"
            name="groupNumber"
            validate={isGroupNumberRequired ? required : undefined}
            data-testid="group-number"
          />

          <FormTextInput
            label="Policy Holder Name*"
            placeholder="Enter policy holder name"
            name="policyHolderName"
            validate={required}
            data-testid="policy-holder-name"
          />

          <FormSingleSelectDropdown
            name="relationshipToPolicyHolder"
            label="Relationship to Policy Holder*"
            validate={required}
            placeholder="Select Relationship to Policy Holder"
            options={RELATIONSHIP_OPTIONS}
            data-testid="relationship-dropdown"
          />
        </>
      )}
    </div>
  );
};
